import type { IDocumentTable } from '@models/Forms/IForms';
import { ValueType } from '@srm/ui/types/ValueType';
import type { IDebugValidationFlags } from './evaluateFieldErrors';
import type { IRequiredMessageField, RequiredDocumentControl } from './requiredMessage';

export type TableFormulaEvaluator = (rule: string, row?: unknown, parentRow?: unknown) => Promise<unknown>;

export type TableValidationRule = 'required' | 'minLength' | 'maxLength' | 'pattern';

export interface ITableValidationIssue {
    tableKey: string;
    rowIndex: number;
    columnKey: string;
    rule: TableValidationRule;
    parent?: ITableValidationIssue;
}

export type TableValidationResult = { valid: true; issue?: undefined } | { valid: false; issue: ITableValidationIssue };

export type ValidationColumn = IRequiredMessageField & {
    key: string;
    controlType: RequiredDocumentControl;
    required?: string;
    visibilityRules?: string;
    min?: string | number;
    max?: string | number;
    maxLength?: number;
    pattern?: string;
    valueType?: ValueType;
    hidden?: boolean;
    name?: string;
    readonly?: string;
    validateLengthAndPattern: boolean;
};

const VALID_RESULT: TableValidationResult = { valid: true };

const asRows = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value) ? value.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object') : [];

const isEmptyRequiredValue = (value: unknown): boolean =>
    value === undefined ||
    value === null ||
    value === '' ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0);

const valueLength = (value: unknown, trim: boolean): number | undefined => {
    if (typeof value === 'string') return (trim ? value.trim() : value).length;
    if (Array.isArray(value)) return value.length;
    return undefined;
};

/**
 * Лимит длины колонки: `max` задаёт правило, `maxLength` — ограничение ввода в редакторе
 * ячейки. Значение из формулы, каскада `changes` или справочника ввод минует, поэтому
 * берём минимум из обоих (паритет с resolveMaxLength для полей формы).
 */
export const resolveColumnMaxLength = (column: {
    max?: string | number | null;
    maxLength?: number;
}): number | undefined => {
    const limits: number[] = [];
    if (column.max !== undefined && column.max !== null && column.max !== '') limits.push(+column.max);
    if (column.maxLength) limits.push(+column.maxLength);
    return limits.length > 0 ? Math.min(...limits) : undefined;
};

export const isValidationColumnVisible = async (
    column: ValidationColumn,
    evaluate: TableFormulaEvaluator,
    parentRow?: unknown,
): Promise<boolean> => {
    // Обычные hidden-колонки TableData оставляет в наборе валидации.
    if (column.hidden) return true;
    if (column.visibilityRules === undefined || column.visibilityRules === null) return true;
    if (!column.visibilityRules) return false;
    return Boolean(await evaluate(column.visibilityRules, undefined, parentRow));
};

export const collectValidationColumns = (table: IDocumentTable): ValidationColumn[] => [
    ...(table.tableColumn ?? []).map((column) => ({
        ...column,
        controlType: 'field' as const,
        validateLengthAndPattern: true,
    })),
    ...(table.tableColumnDict ?? []).map((column) => ({
        ...column,
        controlType: 'dict' as const,
        validateLengthAndPattern: false,
    })),
    ...(table.tableColumnAbook ?? []).map((column) => ({
        ...column,
        controlType: 'abook' as const,
        validateLengthAndPattern: false,
    })),
    ...(table.tableColumnCalc ?? []).map((column) => ({
        ...column,
        controlType: 'calc' as const,
        validateLengthAndPattern: false,
    })),
    ...(table.tableColumnAutoComplete ?? []).map((column) => ({
        ...column,
        controlType: 'autoComplete' as const,
        validateLengthAndPattern: false,
    })),
];

/** Колонка с уже посчитанными на прогон лимитами и скомпилированным regex. */
export type PreparedValidationColumn = ValidationColumn & {
    minLimit?: number;
    maxLimit?: number;
    /** null — pattern задан, но не компилируется: fail-open, как и раньше. */
    patternRe?: RegExp | null;
    canBeRequired: boolean;
};

export const prepareValidationColumn = (column: ValidationColumn): PreparedValidationColumn => {
    const hasMin = column.min !== undefined && column.min !== null && column.min !== '';
    let patternRe: RegExp | null | undefined;
    if (column.pattern) {
        try {
            patternRe = new RegExp(column.pattern);
        } catch (error) {
            // Битый pattern колонки — fail-open, иначе SyntaxError валит весь прогон валидации.
            console.error(`Невалидный pattern "${column.pattern}" колонки ${column.key}:`, error);
            patternRe = null;
        }
    }
    return {
        ...column,
        minLimit: hasMin ? +column.min! : undefined,
        maxLimit: resolveColumnMaxLength(column),
        patternRe,
        canBeRequired:
            column.required !== undefined &&
            column.required !== null &&
            column.required !== 'false' &&
            column.valueType !== ValueType.Boolean,
    };
};

/** Общая проверка ячейки для submit и фоновой сводки заполнения. */
export const evaluateTableCell = async (
    column: PreparedValidationColumn,
    row: Record<string, unknown>,
    evaluate: TableFormulaEvaluator,
    debug: IDebugValidationFlags = {},
    parentRow?: unknown,
): Promise<{ required: boolean; rule?: TableValidationRule }> => {
    if (debug.skipAllValidation) return { required: false };
    const current = row[column.key];
    const required =
        !debug.skipRequiredValidation &&
        column.canBeRequired &&
        (column.required === 'true' || Boolean(await evaluate(column.required!, row, parentRow)));
    if (required && isEmptyRequiredValue(current)) return { required, rule: 'required' };
    if (!column.validateLengthAndPattern) return { required };
    if (!debug.skipLengthValidation) {
        const trimmedLength = valueLength(current, true);
        const untrimmedLength = valueLength(current, false);
        if (
            trimmedLength !== undefined &&
            current !== '' &&
            column.minLimit !== undefined &&
            trimmedLength < column.minLimit
        ) {
            return { required, rule: 'minLength' };
        }
        if (untrimmedLength !== undefined && column.maxLimit !== undefined && untrimmedLength > column.maxLimit) {
            return { required, rule: 'maxLength' };
        }
    }
    if (!debug.skipPatternValidation && column.patternRe) {
        const value = current ?? '';
        const plainObject = typeof value === 'object' && !Array.isArray(value);
        if (!plainObject && !column.patternRe.test(String(value))) return { required, rule: 'pattern' };
    }
    return { required };
};

const makeIssue = (
    table: IDocumentTable,
    rowIndex: number,
    columnKey: string,
    rule: TableValidationRule,
): TableValidationResult => ({
    valid: false,
    issue: { tableKey: table.key, rowIndex, columnKey, rule },
});

/**
 * Кумулятивная проверка таблицы без React и Grid API. Повторяет правила TableData:
 * required для обычных/dict/abook/calc/autocomplete колонок, а длину и pattern —
 * только для обычных колонок. Рекурсивно проверяет все подтаблицы, даже не раскрытые в UI.
 */
export const validateDocumentTable = async (
    table: IDocumentTable,
    value: unknown,
    evaluate: TableFormulaEvaluator,
    debug: IDebugValidationFlags = {},
    parentRow?: unknown,
): Promise<TableValidationResult> => {
    if (debug.skipAllValidation) return VALID_RESULT;
    if (table.visibilityRules && !(await evaluate(table.visibilityRules, undefined, parentRow))) return VALID_RESULT;

    const rows = asRows(value);
    // Лимит длины и regex у колонки одни на все строки: считаем по разу на прогон,
    // а не по разу на ячейку — на таблице в тысячи строк это тысячи лишних new RegExp.
    const columns: PreparedValidationColumn[] = [];
    for (const column of collectValidationColumns(table)) {
        if (await isValidationColumnVisible(column, evaluate, parentRow)) columns.push(prepareValidationColumn(column));
    }

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex];
        for (const column of columns) {
            const { rule } = await evaluateTableCell(column, row, evaluate, debug, parentRow);
            if (rule) return makeIssue(table, rowIndex, column.key, rule);
        }

        for (const subTable of table.tables ?? []) {
            const result = await validateDocumentTable(subTable, row[subTable.key], evaluate, debug, row);
            if (!result.valid) {
                return {
                    valid: false,
                    issue: {
                        tableKey: table.key,
                        rowIndex,
                        columnKey: subTable.key,
                        rule: result.issue.rule,
                        parent: result.issue,
                    },
                };
            }
        }
    }

    return VALID_RESULT;
};
